import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarClock, FileText } from "lucide-react";
import {
  useLegalCases,
  useUpdateLegalCase,
  LEGAL_CASE_STATUS_LABEL,
  LegalCaseStatus,
  legalCaseClientName,
  hasUpcomingHearing,
} from "@/api/legal";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "@/api/client";
import { downloadFile } from "@/utils/downloadFile";
import CreateLegalCaseModal from "./CreateLegalCaseModal";

const STATUS_STYLE: Record<LegalCaseStatus, string> = {
  ACTIVO: "bg-blue-100 text-blue-700",
  SUSPENDIDO: "bg-amber-100 text-amber-700",
  ARCHIVADO: "bg-gray-100 text-gray-400",
  TERMINADO: "bg-green-100 text-green-700",
};

const STATUS_OPTIONS = Object.keys(LEGAL_CASE_STATUS_LABEL) as LegalCaseStatus[];

export default function LegalCasesPage() {
  const [status, setStatus] = useState<LegalCaseStatus | "">("");
  const [search, setSearch] = useState("");
  const [upcomingHearing, setUpcomingHearing] = useState(false);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useLegalCases({
    status: status || undefined,
    search: search || undefined,
    upcomingHearing: upcomingHearing || undefined,
    page,
    pageSize: 15,
  });
  const updateLegalCase = useUpdateLegalCase();

  const legalCases = data?.data ?? [];
  const meta = data?.meta;

  const handleStatusChange = async (id: string, newStatus: LegalCaseStatus) => {
    try {
      await updateLegalCase.mutateAsync({ id, status: newStatus });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleMemorial = async (id: string, title: string) => {
    try {
      await downloadFile(`/legal-cases/${id}/memorial.docx`, `memorial-${title.slice(0, 40)}.docx`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Jurídico</h1>
          <p className="text-sm text-gray-500">Procesos judiciales, administrativos y extrajudiciales por cliente.</p>
        </div>
        {hasPermission("legal.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nuevo caso
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por título, radicado, juzgado o contraparte..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as LegalCaseStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {LEGAL_CASE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={upcomingHearing}
            onChange={(e) => {
              setUpcomingHearing(e.target.checked);
              setPage(1);
            }}
          />
          Con audiencia próxima
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Caso</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Juzgado / entidad</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Próxima audiencia</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && legalCases.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                  No hay casos jurídicos que coincidan con los filtros.
                </td>
              </tr>
            )}
            {legalCases.map((legalCase) => (
              <tr key={legalCase.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{legalCase.title}</p>
                  {legalCase.caseType && <p className="text-xs text-gray-400">{legalCase.caseType}</p>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {legalCase.client ? (
                    <Link to={`/clients/${legalCase.client.id}`} className="hover:text-brand-600">
                      {legalCaseClientName(legalCase)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{legalCase.court || "—"}</td>
                <td className="px-4 py-3">
                  {legalCase.nextHearingDate ? (
                    <span className={hasUpcomingHearing(legalCase) ? "inline-flex items-center gap-1 text-blue-600" : "text-gray-600"}>
                      {hasUpcomingHearing(legalCase) && <CalendarClock size={12} />}
                      {legalCase.nextHearingDate}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {hasPermission("legal.update") ? (
                    <select
                      className={`rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[legalCase.status]}`}
                      value={legalCase.status}
                      onChange={(e) => handleStatusChange(legalCase.id, e.target.value as LegalCaseStatus)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {LEGAL_CASE_STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[legalCase.status]}`}>
                      {LEGAL_CASE_STATUS_LABEL[legalCase.status]}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700"
                    onClick={() => handleMemorial(legalCase.id, legalCase.title)}
                    title="Generar memorial (Word)"
                  >
                    <FileText size={13} className="mr-1" /> Memorial
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} casos)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateLegalCaseModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
